import { motion } from 'framer-motion';
import { FloatingPaths } from '@LandingFunctionalDisplay/BackgroundPaths';
import TopPanel from '@CommonFunctionalDisplay/TopPanel';
import Footer from '@CommonFunctionalDisplay/Footer';
import useSEO from '@Hooks/useSEO';
import './ChangelogPage.scss';

const RELEASES = [
    {
        version: 'v2.0.0',
        date: 'March 15, 2026',
        badge: 'Major Release',
        badgeColor: 'blue',
        summary: 'The biggest release in KaizIQ Resolve history. WhatsApp replies, mobile app launch, and a completely redesigned dashboard.',
        features: [
            { type: 'new', text: 'WhatsApp Reply integration — agents can respond to tickets directly from WhatsApp' },
            { type: 'new', text: 'KaizIQ Desk mobile app — white-label iOS & Android app now generally available' },
            { type: 'new', text: 'Escalation matrix — automatic ticket escalation with configurable rules and timelines' },
            { type: 'new', text: 'Custom ticket statuses — define your own workflow stages beyond the default ones' },
            { type: 'new', text: 'AI-powered ticket routing (beta) — smart assignment based on content analysis' },
            { type: 'improved', text: 'Complete UI redesign — faster, cleaner, and more accessible' },
            { type: 'improved', text: 'Bulk ticket actions — reassign, close, or label dozens of tickets at once' },
            { type: 'improved', text: 'Advanced analytics dashboard with trend graphs and SLA compliance charts' },
            { type: 'fixed', text: 'WhatsApp notification delivery reliability improved to 99.7%' },
            { type: 'fixed', text: 'Resolved form submission timeout on slow connections' },
        ],
    },
    {
        version: 'v1.5.0',
        date: 'January 10, 2026',
        badge: 'Feature Update',
        badgeColor: 'green',
        summary: 'SLA management, label system, and significant performance improvements across the board.',
        features: [
            { type: 'new', text: 'SLA policies — configure response and resolution SLA targets per ticket priority' },
            { type: 'new', text: 'SLA breach alerts — email and WhatsApp warnings before deadlines are missed' },
            { type: 'new', text: 'Label system — tag tickets with custom labels for easy filtering and reporting' },
            { type: 'new', text: 'CSV and PDF export for ticket reports' },
            { type: 'improved', text: 'Ticket list page performance — 60% faster loading on large workspaces' },
            { type: 'improved', text: 'Form builder — added phone number, date picker, and rating field types' },
            { type: 'fixed', text: 'Attachment preview not loading on Safari — now fixed' },
            { type: 'fixed', text: 'Ticket counter badge not updating in real-time on the dashboard' },
        ],
    },
    {
        version: 'v1.2.0',
        date: 'October 22, 2025',
        badge: 'Update',
        badgeColor: 'purple',
        summary: 'Assignment improvements, reporter notifications, and a completely rebuilt onboarding flow.',
        features: [
            { type: 'new', text: 'Auto-assignment rules — route tickets based on category, priority, or keywords' },
            { type: 'new', text: 'Reporter ticket tracking — customers can track their own ticket status via public URL' },
            { type: 'new', text: 'Ticket comments — agents and reporters can exchange messages within a ticket' },
            { type: 'improved', text: 'Onboarding redesigned — new workspaces are ready to receive tickets in under 10 minutes' },
            { type: 'improved', text: 'WhatsApp notification templates — now fully customisable' },
            { type: 'fixed', text: 'Agent invitation emails landing in spam — delivery infrastructure updated' },
        ],
    },
    {
        version: 'v1.0.0',
        date: 'August 1, 2025',
        badge: 'Initial Release',
        badgeColor: 'orange',
        summary: 'The first public release of KaizIQ Resolve. Core ticket management, form builder, and WhatsApp notifications.',
        features: [
            { type: 'new', text: 'No-code form builder with 8 field types' },
            { type: 'new', text: 'Ticket creation, assignment, and status management' },
            { type: 'new', text: 'WhatsApp notification integration for ticket updates' },
            { type: 'new', text: 'Agent workspace with ticket queue and filtering' },
            { type: 'new', text: 'Reporter portal for public ticket submission and tracking' },
            { type: 'new', text: 'Basic dashboard with ticket counts and resolution metrics' },
        ],
    },
];

const BADGE_COLORS = {
    blue:   { bg: 'rgba(26,111,181,0.18)',  text: '#3b9aee' },
    green:  { bg: 'rgba(5,150,105,0.18)',   text: '#10b981' },
    purple: { bg: 'rgba(124,58,237,0.18)',  text: '#a78bfa' },
    orange: { bg: 'rgba(234,88,12,0.18)',   text: '#fb923c' },
};

const CHANGE_TYPES = {
    new:      { label: 'New',      color: '#10b981' },
    improved: { label: 'Improved', color: '#3b9aee' },
    fixed:    { label: 'Fixed',    color: '#a78bfa' },
};

function Reveal({ children, className = '', delay = 0 }) {
    return (
        <motion.div className={className}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay }}
        >{children}</motion.div>
    );
}

export default function ChangelogPage() {
    const cn = 'changelogPage';

    const seo = useSEO({
        title:       "Changelog — What's New in KaizIQ Resolve",
        description: 'Every release, improvement, and fix in KaizIQ Resolve. See the latest features including WhatsApp replies, mobile app, escalation matrix, and more.',
        canonical:   'https://resolve.kaiziq.io/#/changelog',
        schema: {
            '@context': 'https://schema.org',
            '@type':    'WebPage',
            name:       "KaizIQ Resolve Changelog",
            url:        'https://resolve.kaiziq.io/#/changelog',
            description: 'Complete version history and release notes for KaizIQ Resolve.',
        },
    });

    return (
        <div className={cn}>
            {seo}
            <TopPanel />

            <section className={cn + '__hero'}>
                <FloatingPaths position={1} />
                <FloatingPaths position={-1} />
                <div className={cn + '__hero__inner'}>
                    <motion.div className={cn + '__hero__tag'} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
                        Changelog
                    </motion.div>
                    <motion.h1 className={cn + '__hero__title'} initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.1 }}>
                        What's new in KaizIQ Resolve
                    </motion.h1>
                    <motion.p className={cn + '__hero__sub'} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.18 }}>
                        Every release, every improvement, every fix — documented for the teams who rely on us.
                    </motion.p>
                </div>
            </section>

            <section className={cn + '__timeline'}>
                {RELEASES.map((release, ri) => {
                    const badge = BADGE_COLORS[release.badgeColor] || BADGE_COLORS.blue;
                    return (
                        <Reveal key={ri} className={cn + '__release'} delay={ri * 0.08}>
                            <div className={cn + '__release__side'}>
                                <div className={cn + '__release__dot'} />
                                {ri < RELEASES.length - 1 && <div className={cn + '__release__line'} />}
                            </div>
                            <div className={cn + '__release__card'}>
                                <div className={cn + '__release__head'}>
                                    <span className={cn + '__release__version'}>{release.version}</span>
                                    <span className={cn + '__release__badge'} style={{ background: badge.bg, color: badge.text }}>{release.badge}</span>
                                    <span className={cn + '__release__date'}>{release.date}</span>
                                </div>
                                <p className={cn + '__release__summary'}>{release.summary}</p>
                                <ul className={cn + '__release__list'}>
                                    {release.features.map((f, fi) => {
                                        const ct = CHANGE_TYPES[f.type];
                                        return (
                                            <li key={fi} className={cn + '__release__item'}>
                                                <span className={cn + '__release__type'} style={{ color: ct.color, background: ct.color + '18' }}>{ct.label}</span>
                                                <span className={cn + '__release__text'}>{f.text}</span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        </Reveal>
                    );
                })}
            </section>

            <Footer />
        </div>
    );
}
